import { useMemo } from 'react';
import { useApp } from '../../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine } from 'recharts';
import { calculateCompoundInterest, calculateCredit, generateChartData } from '../../utils/calculations';
import { getTaxConfig, INVESTMENT_TYPES } from '../../utils/taxEngine';
import { formatCurrency, formatCompactNumber } from '../../utils/formatters';
import { BarChart3 } from 'lucide-react';

const CustomTooltip = ({ active, payload, label, t, language, formatMoney }) => {
  if (active && payload && payload.length) {
    const isPaused = payload[0]?.payload?.isPaused;
    return (
      <div className="bg-popover/95 backdrop-blur-sm border border-border rounded-xl p-4 shadow-xl">
        <p className="font-semibold mb-2 text-sm flex items-center gap-2">
          {t('year')} {label}
          {isPaused && <span className="text-xs bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300 px-2 py-0.5 rounded">Pauză</span>}
        </p>
        <div className="space-y-1.5 text-sm">
          {payload.map((entry, i) => (
            <div key={i} className="flex justify-between gap-4">
              <span className="text-muted-foreground flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: entry.color }} />
                {entry.name}
              </span>
              <span className="font-semibold">{formatMoney ? formatMoney(entry.value) : formatCurrency(entry.value)}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return null;
};

const CreditChart = ({ data, t, theme, formatMoney, language }) => {
  const isDark = theme === 'dark';
  // Show monthly payment breakdown (principal vs interest) by month
  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id="colorPrincipal" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.4} />
            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
          </linearGradient>
          <linearGradient id="colorInterest" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#ef4444" stopOpacity={0.4} />
            <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#27272a' : '#e2e8f0'} vertical={false} />
        <XAxis dataKey="month" stroke={isDark ? '#71717a' : '#94a3b8'} fontSize={11} tickLine={false} axisLine={false} />
        <YAxis stroke={isDark ? '#71717a' : '#94a3b8'} fontSize={11} tickLine={false} axisLine={false}
          tickFormatter={(v) => `${(v / 1000).toFixed(0)}K`} width={50} />
        <Tooltip formatter={(v) => [formatMoney ? formatMoney(v) : formatCurrency(v)]}
          labelFormatter={(l) => `${language === 'ro' ? 'Luna' : 'Month'} ${l}`}
          contentStyle={{ backgroundColor: isDark ? '#18181b' : '#ffffff', borderRadius: '12px' }} />
        <Legend />
        <Area type="monotone" dataKey="balance" name={language === 'ro' ? 'Sold rămas' : 'Remaining balance'}
          stroke="#3b82f6" strokeWidth={2} fill="url(#colorPrincipal)" animationDuration={800} />
        <Area type="monotone" dataKey="totalInterest" name={language === 'ro' ? 'Dobândă cumulată' : 'Cumulative interest'}
          stroke="#ef4444" strokeWidth={2} fill="url(#colorInterest)" animationDuration={800} />
      </AreaChart>
    </ResponsiveContainer>
  );
};

export const InvestmentChart = () => {
  const { inputs, investmentType, t, theme, language, formatMoney } = useApp();
  const isCredit = investmentType === INVESTMENT_TYPES.CREDIT;

  const chartData = useMemo(() => {
    if (isCredit) {
      const res = calculateCredit({
        loanAmount: inputs.loanAmount ?? 200000,
        annualInterestRate: inputs.creditRate ?? 5,
        termMonths: inputs.creditTermMonths ?? 300,
        monthlyFeeRate: inputs.monthlyFeeRate ?? 0.03,
        gracePeriodMonths: inputs.gracePeriodMonths ?? 0,
        repaymentType: inputs.repaymentType ?? 'annuity',
        extraMonthlyPayment: inputs.extraMonthlyPayment ?? 0,
      });
      return res.monthlyBreakdown || [];
    }

    const taxConfig = getTaxConfig(investmentType);
    const isAccumLike = investmentType === INVESTMENT_TYPES.ACCUMULATION_PLAN ||
      investmentType === INVESTMENT_TYPES.CHILD_FUTURE;
    const res = calculateCompoundInterest({
      initialDeposit: inputs.initialDeposit,
      monthlyContribution: inputs.monthlyContribution,
      annualInterestRate: inputs.annualInterestRate,
      years: inputs.years,
      compoundingFrequency: inputs.compoundingFrequency,
      contributionFrequency: inputs.contributionFrequency,
      taxRate: inputs.includeTaxes ? taxConfig.interestTaxRate : 0,
      taxOnEachPeriod: taxConfig.taxOnEachPeriod,
      adminFeeRate: isAccumLike ? inputs.adminFeeRate : 0,
      pauseStartYear: inputs.pauseEnabled ? inputs.pauseStartYear : 0,
      pauseEndYear: inputs.pauseEnabled ? inputs.pauseEndYear : 0,
      useSimpleInterest: taxConfig.useSimpleInterest,
      topUps: inputs.topUps || [],
      withdrawals: inputs.withdrawals || [],
    });
    return generateChartData(res.yearlyBreakdown);
  }, [inputs, isCredit]);

  const isDark = theme === 'dark';

  const pauseYears = useMemo(() => {
    if (!inputs.pauseEnabled || isCredit) return [];
    return chartData.filter(d => d.isPaused).map(d => d.year);
  }, [chartData, inputs.pauseEnabled, isCredit]);

  return (
    <Card className="bg-card border border-border shadow-sm rounded-2xl overflow-hidden" data-testid="investment-chart">
      <CardHeader className="pb-4 border-b border-border/50">
        <CardTitle className="flex items-center gap-3 text-xl font-light tracking-tight">
          <div className="p-2 rounded-xl bg-gradient-to-br from-blue-500/10 to-emerald-500/10">
            <BarChart3 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          </div>
          {t('chartTitle')}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        <div className="h-[280px] w-full">
          {isCredit ? (
            <CreditChart data={chartData} t={t} theme={theme} formatMoney={formatMoney} language={language} />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorContributions" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.5} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorInterest" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.5} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#27272a' : '#e2e8f0'} vertical={false} />
                <XAxis dataKey="year" stroke={isDark ? '#71717a' : '#94a3b8'} fontSize={11} tickLine={false} axisLine={false}
                  label={{ value: language === 'ro' ? 'Ani' : 'Years', position: 'insideBottom', offset: -5, fontSize: 11 }} />
                <YAxis stroke={isDark ? '#71717a' : '#94a3b8'} fontSize={11} tickLine={false} axisLine={false}
                  tickFormatter={(v) => `${(v / 1000).toFixed(0)}K`} width={55} />
                <Tooltip content={<CustomTooltip t={t} language={language} formatMoney={formatMoney} />} />
                <Legend wrapperStyle={{ paddingTop: '12px' }} />
                {pauseYears.map(y => (
                  <ReferenceLine key={y} x={y} stroke="#f97316" strokeDasharray="4 4" strokeWidth={1.5} />
                ))}
                <Area type="monotone" dataKey="contributions" name={t('contributions')} stroke="#3b82f6" strokeWidth={2} fill="url(#colorContributions)" animationDuration={900} />
                <Area type="monotone" dataKey="interest" name={t('interest')} stroke="#10b981" strokeWidth={2} fill="url(#colorInterest)" animationDuration={900} animationBegin={150} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default InvestmentChart;
